import {
    Theme,
    ComponentsProps,
    ComponentsOverrides,
    ComponentsVariants,
} from '@mui/material';
import { StateLayer, getStateLayerColor } from '../utils/getStayeLayerColor';
import { alpha } from '@mui/material';

declare module '@mui/material/Paper' {
    interface PaperPropsVariantOverrides {
        filled: true;
        default: true;
    }
}

interface M3Card {
    MuiCard: {
        defaultProps?: ComponentsProps['MuiCard'];
        styleOverrides?: ComponentsOverrides<Theme>['MuiCard'];
        variants?: ComponentsVariants['MuiCard'];
    };
    MuiCardContent: {
        defaultProps?: ComponentsProps['MuiCardContent'];
        styleOverrides?: ComponentsOverrides<Theme>['MuiCardContent'];
        variants?: ComponentsVariants['MuiCardContent'];
    };
    MuiCardMedia: {
        defaultProps?: ComponentsProps['MuiCardMedia'];
        styleOverrides?: ComponentsOverrides<Theme>['MuiCardMedia'];
        variants?: ComponentsVariants['MuiCardMedia'];
    };
    MuiCardHeader: {
        defaultProps?: ComponentsProps['MuiCardHeader'];
        styleOverrides?: ComponentsOverrides<Theme>['MuiCardHeader'];
        variants?: ComponentsVariants['MuiCardHeader'];
    };
}

export const getCard = (theme: Theme): M3Card => {
    const { palette } = theme;
    return {
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 3 * 8,
                    // padding: '10px 6px',
                    // '&.MuiPaper-root': {
                    //     marginTop: 0,
                    //     marginBottom: 0,
                    // },
                },
            },
            defaultProps: {
                variant: 'default',
            },
            variants: [
                {
                    props: { variant: 'default' },
                    style: {
                        // margin: 0,
                        // marginTop: 8,
                        // marginBottom: 8,
                    },
                },
                {
                    props: { variant: 'elevation' },
                    style: {
                        boxShadow: theme.shadows[1],
                        backgroundColor: palette.surfaceContainerLow.main,
                        transition: theme.transitions.create(
                            [
                                'background-color',
                                'box-shadow',
                                'border-color',
                                'color',
                            ],
                            {
                                duration: theme.transitions.duration.short,
                            },
                        ),
                        '&:hover': {
                            background: getStateLayerColor(
                                StateLayer.Hover,
                                palette.surfaceContainerLow.main,
                                palette.primary.main,
                            ),
                            boxShadow: theme.shadows[2],
                        },
                        '&:focus': {
                            boxShadow: theme.shadows[1],
                            background: getStateLayerColor(
                                StateLayer.Focus,
                                palette.surfaceContainerLow.main,
                                palette.primary.main,
                            ),
                        },
                        '&:active': {
                            boxShadow: theme.shadows[1],
                            background: getStateLayerColor(
                                StateLayer.Press,
                                palette.surfaceContainerLow.main,
                                palette.primary.main,
                            ),
                        },
                        '&.Mui-disabled': {
                            backgroundColor: alpha(
                                palette.surfaceContainerLow.main,
                                0.38,
                            ),
                            color: palette.surfaceVariant.main,
                            boxShadow: theme.shadows[0],
                        },
                    },
                },
                {
                    props: { variant: 'filled' },
                    style: {
                        boxShadow: theme.shadows[0],
                        backgroundColor: palette.surfaceContainerHighest.main,
                        transition: theme.transitions.create(
                            [
                                'background-color',
                                'box-shadow',
                                'border-color',
                                'color',
                            ],
                            {
                                duration: theme.transitions.duration.short,
                            },
                        ),
                        '&:hover': {
                            background: getStateLayerColor(
                                StateLayer.Hover,
                                palette.surfaceContainerHighest.main,
                                palette.primary.main,
                            ),
                            boxShadow: theme.shadows[1],
                        },
                        '&:focus': {
                            boxShadow: theme.shadows[0],
                            background: getStateLayerColor(
                                StateLayer.Focus,
                                palette.surfaceContainerHighest.main,
                                palette.primary.main,
                            ),
                        },
                        '&:active': {
                            boxShadow: theme.shadows[1],
                            background: getStateLayerColor(
                                StateLayer.Press,
                                palette.surfaceContainerHighest.main,
                                palette.primary.main,
                            ),
                        },
                        '&.Mui-disabled': {
                            backgroundColor: alpha(
                                palette.surfaceContainerHighest.main,
                                0.38,
                            ),
                            color: palette.surfaceVariant.main,
                            boxShadow: theme.shadows[1],
                        },
                    },
                },
                {
                    props: { variant: 'outlined' },
                    style: {
                        boxShadow: theme.shadows[0],
                        backgroundColor: palette.surface.main,
                        borderColor: palette.outlineVariant.main,
                        transition: theme.transitions.create(
                            [
                                'background-color',
                                'box-shadow',
                                'border-color',
                                'color',
                            ],
                            {
                                duration: theme.transitions.duration.short,
                            },
                        ),
                        '&:hover': {
                            background: getStateLayerColor(
                                StateLayer.Hover,
                                palette.surface.main,
                                palette.primary.main,
                            ),
                            boxShadow: theme.shadows[1],
                        },
                        '&:focus': {
                            boxShadow: theme.shadows[0],
                            background: getStateLayerColor(
                                StateLayer.Focus,
                                palette.surface.main,
                                palette.primary.main,
                            ),
                        },
                        '&:active': {
                            boxShadow: theme.shadows[2],
                            background: getStateLayerColor(
                                StateLayer.Press,
                                palette.surfaceContainerHighest.main,
                                palette.primary.main,
                            ),
                        },
                        '&.Mui-disabled': {
                            borderColor: alpha(
                                palette.surfaceContainerHighest.main,
                                0.12,
                            ),
                            boxShadow: theme.shadows[0],
                        },
                    },
                },
            ],
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: 0,
                    margin: 16,
                    '&:last-child': {
                        padding: 0,
                    },
                    // marginRight: 16,
                    // marginLeft: 16,
                    // '&:last-child': {
                    //     marginBottom: 16,
                    // },
                    // '&:first-child': {
                    //     marginTop: 16,
                    // },
                },
            },
        },
        MuiCardMedia: {
            styleOverrides: {
                root: {
                    borderRadius: 3 * 8,
                    overflow: 'hidden',
                },
            },
        },
        MuiCardHeader: {
            defaultProps: {
                titleTypographyProps: { variant: 'titleLarge' },
                subheaderTypographyProps: { variant: 'bodyLarge' },
            },
        },
    };
};
